import { useRef, useState } from 'react'
import { useColorPlacementOptional } from '@/contexts/color-placement-context'
import { usePhotoPlacementOptional } from '@/contexts/photo-placement-context'
import { isGroupShape } from '@/lib/groups/group-kind'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import { cn } from '@/utils'
import { RemoteLockOutline } from './components/remote-lock-outline'
import { ShapeBodyPath } from './components/shape-body-path'
import { ShapeOutline } from './components/shape-outline'
import { ShapePhotoFill } from './components/shape-photo-fill'
import { TextOnPathLayer } from './components/text-on-path-layer'
import { createShapePointerHandlers } from './handlers/shape-pointer-handlers'
import { isFrozenGeometryCursor, shouldUseGroupHoverStyle } from './helpers/shape-body-style'
import { resolveObjectOpacity, resolveShellOpacity } from './helpers/shape-opacity'
import { useFrozenOutlineChrome } from './hooks/use-frozen-outline-chrome'
import { usePhotoFillSizing } from './hooks/use-photo-fill-sizing'
import { useReportedShapeBounds } from './hooks/use-reported-shape-bounds'
import { useShapePath } from './hooks/use-shape-path'
import { useShapeShellLayout } from './hooks/use-shape-shell-layout'
import { useTextOnPathHandleDrag } from './hooks/use-text-on-path-handle-drag'
import { useTextOnPathPreview } from './hooks/use-text-on-path-preview'
import type { ShapeRendererProps } from './types/shape-renderer-props'

/**
 * Draws one shape on the canvas: an absolutely positioned shell `<div>` holding an `<svg>` with the
 * photo fill, the interactive body path, state outlines (remote lock, text editing, frozen hover,
 * alt hover, photo drop target) and any text on a path.
 */
export default function ShapeRenderer({
  shape,
  className,
  onClick,
  onDoubleClick,
  onContextMenu,
  onBoundsChange,
  isInShapeCreationMode = false,
  remoteLocked = false,
  isSelected = false,
  selectionHighlightFrozen = false,
  showPersistentFrozenOutline = false,
  frozenOutlinePulseKey = 0,
  isAltHovered = false,
  isShapeTextEditing = false,
  opacityMultiplier = 1,
  canvasZoom,
  suppressLayoutPixelSnap = false,
  pathLayoutBounds,
  groupHoverStyle,
  textOnPath,
  isTextOnPathEditing = false,
  onTextOnPathLengthChange,
  onTextOnPathStartDistanceChange,
}: ShapeRendererProps) {
  const pathRef = useRef<SVGPathElement>(null)
  const placement = usePhotoPlacementOptional()
  const colorPlacement = useColorPlacementOptional()
  // The shape under a color drag is outlined; it and (for a group) its fillable members preview
  // the color. Same split for the drop: the outline flashes, the recolored shapes hold the tint off.
  const colorDropPreview = colorPlacement?.dropPreview ?? null
  const isColorDropOutlined = colorDropPreview?.shapeId === shape.id
  const colorPreviewFill =
    colorDropPreview?.fillShapeIds.includes(shape.id) === true ? colorDropPreview.fill : null
  const appliedFill = colorPlacement?.appliedFill ?? null
  const colorDropApplied =
    appliedFill != null &&
    (appliedFill.shapeId === shape.id || appliedFill.filledShapeIds.includes(shape.id))
      ? appliedFill
      : null
  const isColorDropFlashing = appliedFill?.shapeId === shape.id
  // The pointer is still over the shape when a color is dropped on it, so the hover tint would
  // cover the new fill. Hold the tint off from the drop until the pointer leaves. Set while
  // rendering (not in an effect) so the tint never shows for even one frame.
  const [hoverTintHeldForKey, setHoverTintHeldForKey] = useState<number | null>(null)
  const [hoverTintHeld, setHoverTintHeld] = useState(false)

  if (colorDropApplied && colorDropApplied.key !== hoverTintHeldForKey) {
    setHoverTintHeldForKey(colorDropApplied.key)
    setHoverTintHeld(true)
  }

  const shapePath = useShapePath(shape)
  const { shellBounds, layoutShell, viewBoxRect, rotationOrigin } = useShapeShellLayout(
    shape,
    pathLayoutBounds,
    canvasZoom,
    suppressLayoutPixelSnap
  )
  const photoFillSizing = usePhotoFillSizing(shape, shellBounds)
  const frozenChrome = useFrozenOutlineChrome(
    frozenOutlinePulseKey,
    isSelected,
    showPersistentFrozenOutline
  )
  const textOnPathPreview = useTextOnPathPreview({
    shapeId: shape.id,
    shapePath,
    textOnPath,
    isEditing: isTextOnPathEditing,
    canvasZoom,
    onPathLengthChange: onTextOnPathLengthChange,
  })
  const textOnPathDrag = useTextOnPathHandleDrag({
    shapeId: shape.id,
    pathRef: textOnPathPreview.pathRef,
    pathLength: textOnPathPreview.pathLength,
    direction: textOnPathPreview.direction,
    onStartDistanceChange: onTextOnPathStartDistanceChange,
  })

  useReportedShapeBounds(pathRef, shape.id, shapePath, shape, onBoundsChange)

  const bodyState = { isInShapeCreationMode, remoteLocked, locked: !!shape.locked, isSelected }
  const groupHoverFill = shouldUseGroupHoverStyle(
    isGroupShape(shape),
    groupHoverStyle != null,
    bodyState
  )
    ? (groupHoverStyle?.fill ?? null)
    : null

  return (
    <div
      className={cn('absolute', className)}
      data-editor-shape-shell="true"
      data-shape-id={shape.id}
      style={{
        left: layoutShell.x,
        top: layoutShell.y,
        width: layoutShell.width,
        height: layoutShell.height,
        transform: shape.rotation ? `rotate(${shape.rotation}deg)` : undefined,
        transformOrigin: rotationOrigin,
        opacity: resolveShellOpacity(opacityMultiplier, remoteLocked),
        overflow: 'visible',
        pointerEvents: 'none',
      }}
    >
      <svg
        // Must match `viewBoxRect` (not a flat `0 0 width height`): the shell div is sized/offset
        // to the pixel-snapped measured path bounds, which can differ from shape.width/height —
        // both because the fill geometry doesn't touch all four edges of its declared box (e.g. a
        // star) or the bbox includes stroke, and because pixel-grid snapping itself shrinks/shifts
        // the box by up to ~1 device px. A mismatched viewBox stretches/shifts the rendered path
        // within the (correctly positioned) shell, showing up as an uneven, seemingly clipped-
        // looking outline.
        viewBox={`${viewBoxRect.x} ${viewBoxRect.y} ${viewBoxRect.width} ${viewBoxRect.height}`}
        data-editor-shape="true"
        style={{
          width: '100%',
          height: '100%',
          overflow: 'visible',
          pointerEvents: 'none',
          display: 'block',
        }}
      >
        <g data-shape-object-layer="true" opacity={resolveObjectOpacity(shape.opacity)}>
          {shape.photoFill && (
            <ShapePhotoFill
              shapeId={shape.id}
              shapePath={shapePath}
              photoFill={shape.photoFill}
              sizing={photoFillSizing}
            />
          )}
          <ShapeBodyPath
            shape={shape}
            shapePath={shapePath}
            pathRef={pathRef}
            state={bodyState}
            isTextFrame={isShapeTextFrame(shape)}
            groupHoverFill={groupHoverFill}
            frozenGeometryCursor={isFrozenGeometryCursor(
              bodyState,
              showPersistentFrozenOutline || selectionHighlightFrozen
            )}
            handlers={createShapePointerHandlers({
              shape,
              remoteLocked,
              onClick,
              onDoubleClick,
              onContextMenu,
            })}
            onPointerEnter={frozenChrome.onPointerEnter}
            onPointerLeave={() => {
              setHoverTintHeld(false)
              frozenChrome.onPointerLeave()
            }}
            suppressHoverTint={colorPreviewFill != null || isColorDropOutlined || hoverTintHeld}
            fillPreview={colorPreviewFill}
          />
        </g>
        {remoteLocked && <RemoteLockOutline width={shape.width} height={shape.height} />}
        {isShapeTextEditing && <ShapeOutline variant="text-edit" shapePath={shapePath} />}
        {frozenChrome.showFrozenHoverChrome && (
          <ShapeOutline
            variant="frozen"
            shapePath={shapePath}
            pulse={frozenChrome.showFrozenOutlinePulse}
          />
        )}
        {isAltHovered && <ShapeOutline variant="alt-hover" shapePath={shapePath} />}
        {placement?.dropTargetShapeId === shape.id && (
          <ShapeOutline variant="drop-target" shapePath={shapePath} />
        )}
        {isColorDropOutlined && <ShapeOutline variant="color-drop-target" shapePath={shapePath} />}
        {isColorDropFlashing && appliedFill && (
          <ShapeOutline key={appliedFill.key} variant="color-drop-applied" shapePath={shapePath} />
        )}
        {textOnPath && (
          <TextOnPathLayer
            shapeId={shape.id}
            shapePath={shapePath}
            config={textOnPath}
            preview={textOnPathPreview}
            isEditing={isTextOnPathEditing}
            draggable={onTextOnPathStartDistanceChange != null}
            drag={textOnPathDrag}
          />
        )}
      </svg>
    </div>
  )
}
